import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Switch
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { ArrowBack, CloudUpload } from '@mui/icons-material';
import { getCampaignApprovedInfluencers, updatePODispatch, getCampaignById, getCampaignFlowById } from '../services/api';
import { toast } from 'react-toastify';
import { openSignedUrl } from '../utils/s3';
import axios from 'axios';

function PODispatch() {
    const { id, stageId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [influencers, setInfluencers] = useState([]);
    const [campaign, setCampaign] = useState(null);
    const [campaignFlow, setCampaignFlow] = useState({});
    const [showSubmitButton, setShowSubmitButton] = useState(false);

    useEffect(() => {
        fetchCampaignFlowData();
        fetchCampaignData();
    }, []);

    useEffect(() => {
        if (campaign) {
            fetchApprovedInfluencers();
        }
    }, [campaign]);

    const fetchCampaignFlowData = async () => {
        try {
            const response = await getCampaignFlowById(stageId);
            const flowData = response.data.data;
            setCampaignFlow(flowData);
            setShowSubmitButton(flowData.status == 0);
        } catch (error) {
            console.error(error);
        }
    };

    const fetchCampaignData = async () => {
        try {
            const response = await getCampaignById(id);
            setCampaign(response.data.data);
        } catch (error) {
            console.error('Failed to fetch campaign:', error);
        }
    };

    const fetchApprovedInfluencers = async () => {
        setLoading(true);
        try {
            const response = await getCampaignApprovedInfluencers(id);
            const data = response.data.data || [];

            setInfluencers(
                data.map(inf => {
                    const poData = campaign?.PODispatch?.find(po => {
                        if (!po?.influencerId) return false;
                        const poId =
                            typeof po.influencerId === 'object'
                                ? po.influencerId._id
                                : po.influencerId;

                        return poId?.toString() === inf._id?.toString();
                    });

                    return {
                        ...inf,
                        sent: poData?.sentToInfluencer === 1 || false,
                        fileName: poData?.uploadPO || ''
                    };
                })
            );
        } catch (error) {
            console.error(error);
            toast.error('Failed to fetch approved influencers');
        } finally {
            setLoading(false);
        }
    };


    const handleSwitchChange = (influencerId, value) => {
        setInfluencers(prev => prev.map(inf =>
            inf._id === influencerId ? { ...inf, sent: value } : inf
        ));
    };

    const handleFileChange = async (event, influencerId) => {
        const file = event.target.files[0];
        if (file) {
            try {
                const formData = new FormData();
                formData.append('file', file);

                const response = await axios.post(`${process.env.REACT_APP_API_URL}/upload`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });

                const uploadedFileName = response.data.filename;
                setInfluencers(prev => prev.map(inf =>
                    inf._id === influencerId ? { ...inf, fileName: uploadedFileName } : inf
                ));
                // toast.success('File uploaded successfully');
            } catch (error) {
                console.error('File upload failed:', error);
                toast.error('File upload failed');
            }
        }
    };

    const handleSubmit = async () => {
        try {
            const poData = influencers.map(inf => ({
                influencerId: inf._id,
                uploadPO: inf.fileName,
                sentToInfluencer: inf.sent ? 1 : 0
            }));

            await updatePODispatch(id, { poData, stageStatus: 11, stageId });
            toast.success('PO Dispatch details submitted successfully');
            navigate(`/marketing/edit/${id}`);
        } catch (error) {
            console.error(error);
            toast.error('Failed to submit PO dispatch details');
        }
    };

    const columns = [
        {
            field: 'sno',
            headerName: 'S.No',
            width: 70,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const index = influencers.findIndex(inf => inf._id === params.row._id);
                return (
                    <Typography variant="body2" fontWeight="medium">
                        {index + 1}
                    </Typography>
                );
            }
        },
        {
            field: 'name',
            headerName: 'Influencer Name',
            width: 300,
            renderCell: (params) => (
                <Typography variant="body2" fontWeight="medium">{params.value}</Typography>
            )
        },
        {
            field: 'upload',
            headerName: 'Upload PO',
            width: 200,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const isEditable = showSubmitButton;
                const fileName = params.row.fileName;

                if (!isEditable && fileName) {
                    return (
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => openSignedUrl(fileName)}
                            sx={{
                                textTransform: 'none'
                            }}
                        >
                            {fileName.length > 12 ? fileName.substring(0, 12) + '...' : fileName}
                        </Button>
                    );
                }

                if (!isEditable) {
                    return (
                        <Typography variant="body2" color="text.secondary">
                            No file
                        </Typography>
                    );
                }

                return (
                    <>
                        <input
                            accept="*"
                            style={{ display: 'none' }}
                            id={`po-upload-file-${params.row._id}`}
                            type="file"
                            onChange={(e) => handleFileChange(e, params.row._id)}
                        />
                        <label htmlFor={`po-upload-file-${params.row._id}`}>
                            <Button
                                variant="contained"
                                component="span"
                                size="small"
                                startIcon={<CloudUpload />}
                                sx={{
                                    bgcolor: '#2196f3',
                                    textTransform: 'none',
                                    maxWidth: '180px',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis'
                                }}
                                title={fileName || "Upload"}
                            >
                                {fileName ? (fileName.length > 12 ? fileName.substring(0, 12) + '...' : fileName) : "Upload"}
                            </Button>
                        </label>
                    </>
                );
            }
        },
        {
            field: 'sent',
            headerName: 'Sent to Influencers',
            width: 200,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const isEditable = showSubmitButton;
                return isEditable ? (
                    <Switch
                        checked={params.value}
                        onChange={(e) => handleSwitchChange(params.row._id, e.target.checked)}
                        color="primary"
                    />
                ) : (
                    <Typography variant="body2" color={params.value ? 'success.main' : 'text.secondary'}>
                        {params.value ? 'Sent' : 'Not Sent'}
                    </Typography>
                );
            }
        }
    ];

    return (
        <Box sx={{ p: 4, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => navigate(`/marketing/edit/${id}`)}
                    sx={{ mb: 2, color: 'text.secondary' }}
                >
                    Back
                </Button>
                <Typography variant="h5" fontWeight="bold" sx={{ color: '#1a1a1a' }}>
                    PO Dispatch
                </Typography>
            </Box>

            <Card sx={{ boxShadow: '0 2px 10px rgba(0,0,0,0.05)', borderRadius: 2 }}>
                <CardContent sx={{ p: 0 }}>
                    <DataGrid
                        rows={influencers}
                        columns={columns}
                        getRowId={(row) => row._id}
                        autoHeight
                        disableSelectionOnClick
                        hideFooter
                        loading={loading}
                        sx={{
                            border: 'none',
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: '#f8f9fa',
                                color: '#666',
                                fontSize: '13px',
                                textTransform: 'uppercase',
                                borderBottom: '1px solid #e0e0e0',
                                fontWeight: 600
                            },
                            '& .MuiDataGrid-cell': {
                                borderBottom: '1px solid #f0f0f0',
                                py: 2
                            },
                            '& .MuiDataGrid-row:hover': {
                                backgroundColor: '#fafafa'
                            }
                        }}
                    />
                </CardContent>
            </Card>

            {showSubmitButton && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        sx={{ px: 4, py: 1 }}
                    >
                        Submit
                    </Button>
                </Box>
            )}
        </Box>
    );
}

export default PODispatch;
